/**
 * MODULE: domain/sales-target-management/frontend/components/SalesTargetSelfDialog
 * PURPOSE: Self-service create/edit dialog for rep quota (amount only on edit)
 * RECENT: 2026-06-21 [2.2.1458] — error toast on failed save via useToast + getErrorMessage.
 */

import { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, TextInput, Select, Label, Spinner, Alert } from 'flowbite-react';
import { useToast } from '@design-system/components';
import { getErrorMessage } from '@shared/lib/frontend/api/errors';
import { Sparkles } from 'lucide-react';
import {
  notifySalesTargetSetOverlay,
} from '@shared/lib/frontend/utils/salesTargetSetBridge';
import { dispatchAssistantOpenWithPrefill } from '@design-system/components/AiAssistant/assistantPrefillBridge';
import { useSalesTargets } from '../hooks/useSalesTargets';
import { useMyTerritoryOptions } from '../hooks/useMyTerritoryOptions';
import { useTenantFiscalCalendar } from '@shared/lib/frontend/hooks/useTenantFiscalCalendar';
import { useTenantDefaultCurrency } from '@shared/lib/frontend/hooks/useTenantDefaultCurrency';
import { currencySymbolForCode } from '@shared/lib/frontend/utils/formatCurrency';
import type { SalesTarget, SalesTargetCreate, PeriodType } from '../../contracts/sales-targets.types';
import {
  calculateSalesTargetPeriodDates,
  currentFiscalQuarter as calendarFiscalQuarter,
  formatSalesTargetPeriodLabel,
} from '../utils/salesTargetPeriodUtils';
import {
  isSalesTargetLocked,
  salesTargetIntegrationHint,
} from '../utils/salesTargetIntegrationUtils';
import { SalesTargetIntegrationBadge } from './SalesTargetIntegrationBadge';

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  target?: SalesTarget | null;
  defaultPeriodType?: PeriodType;
  defaultFiscalYear?: number;
  defaultFiscalQuarter?: number;
  initialTargetAmount?: number;
  showAiCoachStrip?: boolean;
  coachInAssistant?: boolean;
  onSaved?: () => void;
};

export function SalesTargetSelfDialog({
  open,
  onClose,
  userId,
  target,
  defaultPeriodType = 'quarterly',
  defaultFiscalYear,
  defaultFiscalQuarter,
  initialTargetAmount,
  showAiCoachStrip = false,
  coachInAssistant = false,
  onSaved,
}: Props) {
  const { createTarget, updateTarget } = useSalesTargets();
  const { showToast } = useToast();
  const { data: territories = [] } = useMyTerritoryOptions();
  const { currentFiscalYear: tenantFiscalYear, currentFiscalQuarter: tenantFiscalQuarter } =
    useTenantFiscalCalendar();
  const resolvedDefaultFiscalYear = defaultFiscalYear ?? tenantFiscalYear;
  const resolvedDefaultFiscalQuarter = defaultFiscalQuarter ?? tenantFiscalQuarter;
  const { currency: tenantCurrency, locale: tenantLocale } = useTenantDefaultCurrency();
  const currencySymbol = currencySymbolForCode(tenantCurrency, tenantLocale);
  const isEditing = Boolean(target);

  const [territoryId, setTerritoryId] = useState<string>('');
  const [periodType, setPeriodType] = useState<PeriodType>(defaultPeriodType);
  const [fiscalYear, setFiscalYear] = useState(resolvedDefaultFiscalYear);
  const [fiscalQuarter, setFiscalQuarter] = useState(resolvedDefaultFiscalQuarter);
  const [targetAmount, setTargetAmount] = useState<number>(initialTargetAmount ?? 0);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setTerritoryId(target.territory_id ?? '');
      setPeriodType(target.period_type);
      setFiscalYear(target.fiscal_year);
      setFiscalQuarter(target.fiscal_quarter ?? calendarFiscalQuarter());
      setTargetAmount(target.target_amount);
    } else {
      setTerritoryId(territories[0]?.id ?? '');
      setPeriodType(defaultPeriodType);
      setFiscalYear(resolvedDefaultFiscalYear);
      setFiscalQuarter(resolvedDefaultFiscalQuarter);
      setTargetAmount(initialTargetAmount ?? 0);
    }
  }, [
    open,
    target,
    territories,
    defaultPeriodType,
    resolvedDefaultFiscalYear,
    resolvedDefaultFiscalQuarter,
    initialTargetAmount,
  ]);

  const periodLabel = target ? formatSalesTargetPeriodLabel(target) : `Q${fiscalQuarter} ${fiscalYear}`;

  useEffect(() => {
    if (!open) {
      notifySalesTargetSetOverlay(null);
      return;
    }
    notifySalesTargetSetOverlay({
      modal: 'sales_target_set',
      mode: isEditing ? 'edit' : 'create',
      periodLabel,
      targetAmount: targetAmount > 0 ? targetAmount : undefined,
      fiscalYear,
      fiscalQuarter,
    });
  }, [open, isEditing, periodLabel, targetAmount, fiscalYear, fiscalQuarter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && target) {
        await updateTarget.mutateAsync({
          id: target.id,
          data: { target_amount: targetAmount },
        });
      } else {
        const dates = calculateSalesTargetPeriodDates(
          periodType,
          fiscalYear,
          periodType === 'quarterly' ? fiscalQuarter : undefined
        );
        const payload: SalesTargetCreate = {
          user_id: userId,
          territory_id: territoryId || null,
          period_type: periodType,
          fiscal_year: fiscalYear,
          fiscal_quarter: periodType === 'quarterly' ? fiscalQuarter : null,
          fiscal_month: null,
          period_start_date: dates.period_start_date,
          period_end_date: dates.period_end_date,
          target_amount: targetAmount,
          target_currency: tenantCurrency,
          status: 'active',
        };
        await createTarget.mutateAsync(payload);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save quota:', error);
      showToast({
        message: getErrorMessage(error, 'Failed to save quota. Please try again.'),
        variant: 'error',
      });
    }
  };

  const isPending = createTarget.isPending || updateTarget.isPending;
  const locked = isEditing && isSalesTargetLocked(target);
  const integrationHint = target ? salesTargetIntegrationHint(target) : null;

  return (
    <Modal show={open} onClose={onClose} size="md" data-testid="sales-target-set-modal">
      <ModalHeader>{isEditing ? 'Edit your quota' : 'Set your quota'}</ModalHeader>
      <ModalBody>
        {(showAiCoachStrip || coachInAssistant) && (
          <div
            className="mb-4 rounded-lg border border-violet-200/80 bg-gradient-to-br from-violet-50 to-white p-3 dark:border-violet-800/50 dark:from-violet-950/40 dark:to-gray-900"
            data-testid="sales-target-ai-coach-strip"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-300" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">AI quota coach</p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  The sidebar assistant sees this form while it is open. Ask for a recommended amount,
                  sanity-check against pipeline, or compare to territory plan.
                </p>
                <Button
                  size="xs"
                  color="light"
                  className="mt-2"
                  data-testid="sales-target-ask-ai-in-modal"
                  onClick={() =>
                    dispatchAssistantOpenWithPrefill(
                      'Based on my pipeline and territory, what quota amount should I set for this quarter?',
                      { autoSend: false }
                    )
                  }
                >
                  Ask AI for a recommendation
                </Button>
              </div>
            </div>
          </div>
        )}
        {locked && (
          <Alert color="warning" className="mb-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span>This quota is managed externally.</span>
                <SalesTargetIntegrationBadge target={target!} />
              </div>
              {integrationHint && <p className="text-sm">{integrationHint}</p>}
            </div>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isEditing
              ? 'Update your revenue target for this period. Official quotas may be synced from your CRM.'
              : 'Set a revenue target for your sales period. You can adjust it here until your organization connects an external quota source.'}
          </p>

          {isEditing ? (
            <div>
              <Label>Period</Label>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{periodLabel}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="self-fiscal-year">Fiscal year</Label>
                  <TextInput
                    id="self-fiscal-year"
                    type="number"
                    min={2020}
                    max={2035}
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="self-fiscal-quarter">Quarter</Label>
                  <Select
                    id="self-fiscal-quarter"
                    value={fiscalQuarter}
                    onChange={(e) => setFiscalQuarter(parseInt(e.target.value, 10))}
                  >
                    <option value={1}>Q1</option>
                    <option value={2}>Q2</option>
                    <option value={3}>Q3</option>
                    <option value={4}>Q4</option>
                  </Select>
                </div>
              </div>
              {territories.length > 0 && (
                <div>
                  <Label htmlFor="self-territory">Territory (optional)</Label>
                  <Select
                    id="self-territory"
                    value={territoryId}
                    onChange={(e) => setTerritoryId(e.target.value)}
                  >
                    <option value="">No territory</option>
                    {territories.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.territory_code})
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </>
          )}

          <div>
            <Label htmlFor="self-target-amount">
              Quota amount ({currencySymbol} {tenantCurrency}) *
            </Label>
            <TextInput
              id="self-target-amount"
              type="number"
              min={0}
              step={1000}
              value={targetAmount || ''}
              onChange={(e) => setTargetAmount(parseFloat(e.target.value) || 0)}
              required
              disabled={locked}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button color="gray" onClick={onClose} disabled={isPending}>Cancel</Button>
            {!locked && (
              <Button type="submit" color="blue" disabled={isPending}>
                {isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving…
                  </>
                ) : isEditing ? (
                  'Save quota'
                ) : (
                  'Create quota'
                )}
              </Button>
            )}
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}
